import { padTimePart } from './customDatePicker.utils';

const ChevronUpIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
  </svg>
);

export const SPINNER_KIND = {
  HOURS: 'hours',
  MINUTES: 'minutes',
} as const;

export type SpinnerKind = (typeof SPINNER_KIND)[keyof typeof SPINNER_KIND];

const SPINNER_MAX: Record<SpinnerKind, number> = {
  [SPINNER_KIND.HOURS]: 23,
  [SPINNER_KIND.MINUTES]: 59,
};

const SPINNER_PLACEHOLDER: Record<SpinnerKind, string> = {
  [SPINNER_KIND.HOURS]: 'HH',
  [SPINNER_KIND.MINUTES]: 'mm',
};

export type TimeSpinnerProps = {
  kind: SpinnerKind;
  value: string;
  invalid: boolean;
  'aria-label': string;
  onChange: (val: string) => void;
  onCommit: (freshVal: string) => void;
};

export const TimeSpinner = ({
  kind,
  value,
  invalid,
  onChange,
  onCommit,
  ...rest
}: TimeSpinnerProps) => {
  const max = SPINNER_MAX[kind];

  const step = (dir: 1 | -1) => {
    const current = parseInt(value, 10);
    const base = isNaN(current) ? 0 : current;
    const next = (base + dir + max + 1) % (max + 1);
    const nextStr = padTimePart(next);
    onChange(nextStr);
    onCommit(nextStr);
  };

  return (
    <div className="custom-date-time-spinner">
      <button
        type="button"
        className="custom-date-time-spinner__btn"
        aria-label={`Increase ${rest['aria-label']}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => step(1)}
      >
        <ChevronUpIcon />
      </button>
      <input
        type="text"
        inputMode="numeric"
        maxLength={2}
        className={`custom-date-calendar-time__input${invalid ? ' is-invalid' : ''}`}
        value={value}
        placeholder={SPINNER_PLACEHOLDER[kind]}
        aria-label={rest['aria-label']}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 2))}
        onBlur={() => onCommit(value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            step(1);
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            step(-1);
          }
        }}
      />
      <button
        type="button"
        className="custom-date-time-spinner__btn"
        aria-label={`Decrease ${rest['aria-label']}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => step(-1)}
      >
        <ChevronDownIcon />
      </button>
    </div>
  );
};
